import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orderAPI } from '../../services/api';
import OrderForm from './OrderForm';
import ScheduleOrderModal from './ScheduleOrderModal';
import SplitOrderModal from './SplitOrderModal';
import '../Customers/Customers.css';

const OrderListPage = ({ user }) => {
  const navigate = useNavigate();
  const { action, id } = useParams();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [sortBy, setSortBy] = useState('order_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterPayment, setFilterPayment] = useState('');
  const [showScheduled, setShowScheduled] = useState(false);
  const [scheduleDateFrom, setScheduleDateFrom] = useState('');
  const [scheduleDateTo, setScheduleDateTo] = useState('');

  // Modal states for viewing/editing
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Modal state for scheduling
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [orderToSchedule, setOrderToSchedule] = useState(null);

  // Modal state for splitting order
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [orderToSplit, setOrderToSplit] = useState(null);

  // Handle URL-based modal opening
  useEffect(() => {
    if (action === 'new') {
      setIsEditMode(false);
      setSelectedOrderId(null);
      setShowOrderForm(true);
    } else if (action === 'edit' && id) {
      setIsEditMode(true);
      setSelectedOrderId(id);
      setShowOrderForm(true);
    } else if (action === 'view' && id) {
      setSelectedOrderId(id);
      setShowOrderDetail(true);
    } else {
      setShowOrderDetail(false);
      setShowOrderForm(false);
    }
  }, [action, id]);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, search, sortBy, sortOrder, filterPayment, showScheduled, scheduleDateFrom, scheduleDateTo]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        per_page: 15,
        has_schedule_date: showScheduled ? true : false,
      };

      if (filterPayment !== '') {
        params.payment_received = filterPayment === 'true';
      }

      // Add schedule date range filters when viewing scheduled orders
      if (showScheduled) {
        if (scheduleDateFrom) {
          params.schedule_date_from = scheduleDateFrom;
        }
        if (scheduleDateTo) {
          params.schedule_date_to = scheduleDateTo;
        }
      }

      const response = await orderAPI.getAll(params);
      setOrders(response.data);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        total: response.total,
        per_page: response.per_page,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load orders');
      console.error('Error fetching orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchOrders();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }

    try {
      await orderAPI.delete(orderId);
      fetchOrders();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete order');
    }
  };

  const handleTogglePayment = async (orderId) => {
    try {
      await orderAPI.togglePayment(orderId);
      fetchOrders();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update payment status');
    }
  };

  const handleView = (orderId) => {
    navigate(`/orders/view/${orderId}`);
  };

  const handleViewDelivery = (deliveryRunId) => {
    navigate(`/deliveries/planning?run=${deliveryRunId}`);
  };

  const handleEdit = (orderId) => {
    navigate(`/orders/edit/${orderId}`);
  };

  const handleSchedule = (order) => {
    setOrderToSchedule(order);
    setShowScheduleModal(true);
  };

  const handleSplit = async (order) => {
    try {
      // Fetch full order details with order lines
      const fullOrder = await orderAPI.getById(order.id);
      setOrderToSplit(fullOrder);
      setShowSplitModal(true);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load order details');
    }
  };

  const handleNew = () => {
    navigate('/orders/new');
  };

  const closeModal = () => {
    navigate('/orders');
    setShowOrderDetail(false);
    setShowOrderForm(false);
    setSelectedOrderId(null);
  };

  const handleFormSuccess = () => {
    closeModal();
    fetchOrders();
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const getTotalItems = (order) => {
    if (!order.order_lines) return 0;
    return order.order_lines.reduce((sum, line) => sum + (line.qty || 0), 0);
  };

  const getTotalValue = (order) => {
    if (!order.order_lines) return 0;
    return order.order_lines.reduce((sum, line) => sum + ((line.unit_price || 0) * (line.qty || 0)), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(amount);
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>{showScheduled ? 'Scheduled Orders' : 'Unscheduled Orders'}</h1>
              <div className="header-actions">
                <button
                  className="btn btn-primary"
                  onClick={handleNew}
                >
                  + New Order
                </button>
              </div>
            </div>

            <div className="customer-search">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Search by reference, address, or customer..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="btn btn-secondary">
                  Search
                </button>
                {search && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSearch('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
              <div className="filters-row">
                <div className="filter-group">
                  <label class="filter-label">Payment: </label>
                  <select
                    value={filterPayment}
                    onChange={(e) => {
                      setFilterPayment(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="filter-select"
                  >
                    <option value="">All</option>
                    <option value="true">Paid</option>
                    <option value="false">Unpaid</option>
                  </select>
                  
                </div>
                <div className="filter-group">

                  <button
                    className={`btn ${showScheduled ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}`}
                    onClick={() => {
                      setShowScheduled(false);
                      setCurrentPage(1);
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    Unscheduled
                  </button>
                  <button
                    className={`btn ${showScheduled ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}`}
                    onClick={() => {
                      setShowScheduled(true);
                      setCurrentPage(1);
                    }}
                    style={{ marginRight: '8px' }}
                  >
                    Scheduled
                  </button>
                </div>
              </div>

              {showScheduled && (
                <div className="filters-row">
                  <div className="filter-group schedule-date-filter">
                    <label>Schedule Date: </label>
                    <input
                      type="date"
                      value={scheduleDateFrom}
                      onChange={(e) => {
                        setScheduleDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                      placeholder="From"
                    />
                    <span> to </span>
                    <input
                      type="date"
                      value={scheduleDateTo}
                      onChange={(e) => {
                        setScheduleDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                      placeholder="To"
                    />
                    {(scheduleDateFrom || scheduleDateTo) && (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => {
                          setScheduleDateFrom('');
                          setScheduleDateTo('');
                          setCurrentPage(1);
                        }}
                        style={{ marginLeft: '8px' }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
              
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading && orders.length === 0 ? (
              <div className="loading">Loading orders...</div>
            ) : (
              <>
                <div className="customer-table-container">
                  <table className="customer-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('order_date')} className="sortable">
                          Date {getSortIcon('order_date')}
                        </th>
                        {showScheduled && (
                          <th onClick={() => handleSort('schedule_date')} className="sortable">
                            Schedule {getSortIcon('schedule_date')}
                          </th>
                        )}
                        <th onClick={() => handleSort('order_external_reference')} className="sortable">
                          Reference {getSortIcon('order_external_reference')}
                        </th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Total Value</th>
                        <th>Delivery Address</th>
                        <th>Payment</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan={showScheduled ? "9" : "8"} className="no-data">
                            No orders found
                          </td>
                        </tr>
                      ) : (
                        orders.map((order) => (
                          <tr key={order.id}>
                            <td data-label="Date">{formatDate(order.order_date)}</td>
                            {showScheduled && (
                              <td data-label="Schedule">{formatDate(order.schedule_date)}</td>
                            )}
                            <td data-label="Reference">
                              {order.order_external_reference || `-`}
                            </td>
                            <td data-label="Customer">
                              {order.customer ? order.customer.fullname : '-'}
                            </td>
                            <td data-label="Items">
                              <span className="badge badge-info">{getTotalItems(order)} items</span>
                            </td>
                            <td data-label="Total Value" className="text-right">
                              {formatCurrency(getTotalValue(order))}
                            </td>
                            <td data-label="Address" className="address-cell">
                              {order.delivery_address || '-'}
                            </td>
                            <td data-label="Payment">
                              <span
                                className={`badge ${order.payment_received ? 'badge-success' : 'badge-warning'}`}
                              >
                                {order.payment_received ? 'Paid' : 'Unpaid'}
                              </span>
                              {order.payment_method && (
                                <span className="payment-method"> ({order.payment_method})</span>
                              )}
                            </td>
                            <td data-label="Actions" className="actions-cell">
                              {!showScheduled && (
                                <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSchedule(order)}
                              >
                                Schedule
                              </button>
                              )}
                              <div>
                              {order.deliveries && order.deliveries.length > 0 ? (
                                order.deliveries.map((delivery) => (
                                  delivery.delivery_run_id && (
                                    <button
                                      key={delivery.id}
                                      className="btn btn-sm btn-info"
                                      onClick={() => handleViewDelivery(delivery.delivery_run_id)}
                                      style={{ marginRight: '4px' }}
                                    >
                                      View Run
                                    </button>
                                  )
                                ))
                              ) : null}
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleEdit(order.id)}
                              >
                                Edit
                              </button>
                              <button
                                className={`btn btn-sm ${order.payment_received ? 'btn-success' : 'btn-success'}`}
                                onClick={() => handleTogglePayment(order.id)}
                                title={order.payment_received ? 'Mark as unpaid' : 'Mark as paid'}
                              >
                                {order.payment_received ? 'Unpay' : 'Pay'}
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(order.id)}
                              >
                                Delete
                              </button>
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleSplit(order)}
                                disabled={!order.order_lines || order.order_lines.length === 0}
                              >
                                Split
                              </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination && pagination.last_page > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {pagination.current_page} of {pagination.last_page}
                      ({pagination.total} total)
                    </span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.last_page}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrderId && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Order Details</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <div className="modal-body">
              {orders.find(o => o.id === selectedOrderId) ? (
                <OrderDetailView order={orders.find(o => o.id === selectedOrderId)} />
              ) : (
                <p>Loading...</p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>Close</button>
              {selectedOrderId && (
                <button
                  className="btn btn-warning"
                  onClick={() => {
                    closeModal();
                    navigate(`/orders/edit/${selectedOrderId}`);
                  }}
                >
                  Edit
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Form Modal */}
      {showOrderForm && (
        <OrderForm
          orderId={isEditMode ? selectedOrderId : null}
          onClose={closeModal}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Schedule Order Modal */}
      <ScheduleOrderModal
        isOpen={showScheduleModal}
        onClose={() => {
          setShowScheduleModal(false);
          setOrderToSchedule(null);
        }}
        order={orderToSchedule}
        onScheduled={() => {
          fetchOrders();
        }}
      />

      {/* Split Order Modal */}
      <SplitOrderModal
        isOpen={showSplitModal}
        onClose={() => {
          setShowSplitModal(false);
          setOrderToSplit(null);
        }}
        order={orderToSplit}
        onSplitComplete={() => {
          fetchOrders();
        }}
      />
    </div>
  );
};

// Simple order detail view component
const OrderDetailView = ({ order }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="order-detail">
      <div className="detail-row">
        <strong>Order Date:</strong> {formatDate(order.order_date)}
      </div>
      {order.schedule_date && (
        <div className="detail-row">
          <strong>Schedule Date:</strong> {formatDate(order.schedule_date)}
        </div>
      )}
      {order.order_external_reference && (
        <div className="detail-row">
          <strong>Reference:</strong> {order.order_external_reference}
        </div>
      )}
      <div className="detail-row">
        <strong>Customer:</strong> {order.customer?.fullname || '-'}
      </div>
      <div className="detail-row">
        <strong>Delivery Address:</strong> {order.delivery_address || '-'}
      </div>
      <div className="detail-row">
        <strong>Payment:</strong>
        <span className={`badge ${order.payment_received ? 'badge-success' : 'badge-warning'}`}>
          {order.payment_received ? 'Paid' : 'Unpaid'}
        </span>
        {order.payment_method && <span> ({order.payment_method})</span>}
      </div>
      {order.note && (
        <div className="detail-row">
          <strong>Notes:</strong> {order.note}
        </div>
      )}

      {order.order_lines && order.order_lines.length > 0 && (
        <div className="order-lines">
          <h4>Order Items</h4>
          <table className="detail-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Size</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              {order.order_lines.map((line, index) => (
                <tr key={index}>
                  <td>{line.product?.description || line.product_id}</td>
                  <td>{line.qty}</td>
                  <td>{line.size || '-'}</td>
                  <td>{line.weight_code || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default OrderListPage;